import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { scenarioKeys } from './useScenarios';
import type { ScenarioSummary } from '../api/types';

/**
 * Blueprint hooks — the catalog of read-only scenario templates bundled with
 * the platform, plus the mutation that clones one into a tenant-private
 * scenario the trainee/instructor can edit.
 *
 * The list endpoint is the same `GET /scenarios` as the catalog page — what
 * differs is the call site: blueprints expose the "Duplicate" affordance and
 * highlight `is_builtin: true` items first.
 */

export const blueprintKeys = {
  all: ['blueprints'] as const,
  list: () => [...blueprintKeys.all, 'list'] as const,
};

export function useBlueprints() {
  return useQuery<ReadonlyArray<ScenarioSummary>>({
    queryKey: blueprintKeys.list(),
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<unknown>('/scenarios', { method: 'GET', signal });
      const items = Array.isArray(resp)
        ? (resp as ReadonlyArray<ScenarioSummary>)
        : (resp as { items?: ReadonlyArray<ScenarioSummary> })?.items ?? [];
      // Built-ins surface first — tenant-private clones sort after.
      const builtin = items.filter((s) => (s as { is_builtin?: boolean }).is_builtin !== false);
      const others = items.filter((s) => (s as { is_builtin?: boolean }).is_builtin === false);
      return [...builtin, ...others];
    },
    staleTime: 60_000,
  });
}

export interface CloneBlueprintInput {
  sourceSlug: string;
  /** Optional human-friendly name for the clone; defaults to "<original> (copy)". */
  name?: string;
  /** Optional explicit slug; defaults to `t<tenant>-<source>-<short_id>`. */
  slug?: string;
}

export interface CloneBlueprintResponse {
  slug: string;
  sourceSlug: string;
  name: string;
  tenantId: number;
  status: 'cloned';
}

export function useCloneBlueprint() {
  const qc = useQueryClient();
  return useMutation<CloneBlueprintResponse, Error, CloneBlueprintInput>({
    mutationFn: async (input) =>
      apiFetch<CloneBlueprintResponse>('/scenarios/{slug}/clone', {
        method: 'POST',
        pathParams: { slug: input.sourceSlug },
        body: {
          ...(input.name !== undefined ? { name: input.name } : {}),
          ...(input.slug !== undefined ? { slug: input.slug } : {}),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: blueprintKeys.list() });
      qc.invalidateQueries({ queryKey: scenarioKeys.all });
    },
  });
}
